import {
    Component,
    Input,
    OnChanges,
    SimpleChanges,
    AfterViewInit,
    OnDestroy,
    ElementRef,
    ViewChild,
    NgZone,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
    Chart,
    BarController,
    BarElement,
    CategoryScale,
    LinearScale,
    Tooltip,
    Legend,
} from 'chart.js';
import { ChartSerie } from '../chart-horizontal-bar/chart-horizontal-bar';

Chart.register(BarController, BarElement, CategoryScale, LinearScale, Tooltip, Legend);

@Component({
    selector: 'app-chart-vertical-bar',
    standalone: true,
    imports: [CommonModule],
    template: `
        <div class="relative w-full" [style.height.px]="chartHeight">
            <canvas #chartCanvas></canvas>
        </div>
    `,
})
export class ChartVerticalBar implements AfterViewInit, OnChanges, OnDestroy {

    @ViewChild('chartCanvas') canvasRef!: ElementRef<HTMLCanvasElement>;

    @Input() categories: string[] = [];
    @Input() series: ChartSerie[] = [];
    @Input() activeSeries: string[] = [];
    @Input() stacked: boolean = false;
    /** Fixed height in px — if provided, overrides auto calculation */
    @Input() height?: number;

    private chart: Chart | null = null;

    get chartHeight(): number {
        if (this.height) return this.height;
        return 220;
    }

    constructor(private zone: NgZone) { }

    ngAfterViewInit(): void {
        this.zone.runOutsideAngular(() => this.buildChart());
    }

    ngOnChanges(changes: SimpleChanges): void {
        // Si llegan categorías nuevas (de vacío a con datos), reconstruir el gráfico completo
        if (changes['categories']) {
            const prev = changes['categories'].previousValue as string[];
            const curr = changes['categories'].currentValue as string[];
            if (curr?.length > 0 && (prev?.length === 0 || !prev)) {
                this.zone.runOutsideAngular(() => {
                    this.chart?.destroy();
                    this.chart = null;
                    this.buildChart();
                });
                return;
            }
        }
        if (this.chart && (changes['series'] || changes['activeSeries'] || changes['categories'])) {
            this.zone.runOutsideAngular(() => this.updateChart());
        }
    }

    ngOnDestroy(): void {
        this.chart?.destroy();
    }

    private buildChart(): void {
        if (!this.canvasRef) return;
        const ctx = this.canvasRef.nativeElement.getContext('2d');
        if (!ctx) return;

        this.chart = new Chart(ctx, {
            type: 'bar',
            data: {
                labels: this.categories,
                datasets: this.buildDatasets(),
            },
            options: this.buildOptions(),
            plugins: [this.getDataLabelsPlugin()],
        });
    }

    private updateChart(): void {
        if (!this.chart) return;
        this.chart.data.labels = this.categories;
        this.chart.data.datasets = this.buildDatasets();
        this.chart.update();
    }

    private buildDatasets() {
        const filtered = this.series.filter(s => this.activeSeries.includes(s.id));
        return filtered.map((s, idx) => ({
            label: s.label,
            data: s.data,
            backgroundColor: s.color + 'D9',
            borderColor: s.color,
            borderWidth: this.stacked ? 0 : 1.5,
            borderRadius: this.stacked
                ? (idx === filtered.length - 1 ? { topLeft: 6, topRight: 6 } : 0)
                : 6,
            borderSkipped: false,
        }));
    }

    private buildOptions(): any {
        return {
            indexAxis: 'x',          // ← barras verticales
            responsive: true,
            maintainAspectRatio: false,
            animation: { duration: 500, easing: 'easeInOutQuart' },
            plugins: {
                legend: { display: false },
                tooltip: {
                    mode: this.stacked ? 'index' : 'nearest',
                    callbacks: {
                        label: (ctx: any) => {
                            const serie = this.series.find(s => s.label === ctx.dataset.label);
                            const pct = ctx.parsed.y?.toFixed(0) ?? '0';
                            if (serie?.rawData) {
                                const abs = serie.rawData[ctx.dataIndex] ?? 0;
                                return ` ${ctx.dataset.label}: ${pct}%  (${abs} alumnos)`;
                            }
                            return ` ${ctx.dataset.label}: ${pct}%`;
                        },
                    },
                    backgroundColor: 'rgba(15,23,42,0.92)',
                    titleColor: '#f8fafc',
                    bodyColor: '#cbd5e1',
                    padding: 12,
                    cornerRadius: 10,
                    titleFont: { size: 12, weight: 'bold' },
                    bodyFont: { size: 12 },
                },
            },
            scales: {
                x: {
                    stacked: this.stacked,
                    ticks: {
                        color: '#475569',
                        font: { size: 11, weight: '600' },
                        maxRotation: 30,
                    },
                    grid: { display: false },
                    border: { display: false },
                },
                y: {
                    stacked: this.stacked,
                    min: 0,
                    max: 100,
                    ticks: {
                        callback: (v: any) => v + '%',
                        color: '#94a3b8',
                        font: { size: 11 },
                    },
                    grid: { color: 'rgba(0,0,0,0.06)' },
                    border: { display: false },
                },
            },
        };
    }

    private getDataLabelsPlugin(): any {
        return {
            id: 'customDataLabels',
            afterDatasetsDraw: (chart: any) => {
                const { ctx } = chart;
                chart.data.datasets.forEach((dataset: any, i: number) => {
                    const meta = chart.getDatasetMeta(i);
                    if (meta.hidden) return;
                    meta.data.forEach((element: any, index: number) => {
                        const data = dataset.data[index];
                        if (typeof data === 'number' && data > 0) {
                            const text = Math.round(data) + '%';
                            ctx.save();
                            ctx.font = 'bold 11px sans-serif';
                            ctx.textAlign = 'center';
                            
                            const isSmall = data < 6;
                            ctx.fillStyle = isSmall ? '#475569' : '#ffffff';
                            
                            if (isSmall) {
                                ctx.textBaseline = 'bottom';
                                ctx.fillText(text, element.x, element.y - 4);
                            } else {
                                ctx.textBaseline = 'middle';
                                ctx.fillText(text, element.x, (element.base + element.y) / 2);
                            }
                            ctx.restore();
                        }
                    });
                });
            }
        };
    }
}
